/**
 * WeightMaterialSection — right column for weight-based material input.
 * Extracted from AddSessionModal.tsx for readability.
 */
import React, { useMemo } from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { type RawMaterial, type MaterialSummary, type LocationBalance } from '../../../../services/productionService';
import { type ByproductMapping } from '../../../../services/byproductMappingService';
import { formatYield, isYieldFallback } from '../../../../hooks/useYieldFactor';
import { Trash2, ArrowUpDown, AlertTriangle } from 'lucide-react';
import type { Product, WeightMaterialEntry, MaterialSortOption } from './types';
import RawMaterialOverview from './RawMaterialOverview';
import MaterialSummaryBoxes from './MaterialSummaryBoxes';

interface WeightMaterialSectionProps {
  weightEntries: WeightMaterialEntry[];
  setWeightEntries: React.Dispatch<React.SetStateAction<WeightMaterialEntry[]>>;
  productSku: string;
  products: Product[];
  rawMaterials: RawMaterial[];
  sortedRawMaterials: RawMaterial[];
  loadingMaterials: boolean;
  materialSortOption: MaterialSortOption;
  setMaterialSortOption: (v: MaterialSortOption) => void;
  byproductMappings: ByproductMapping[];
  materialSummary: MaterialSummary;
  icsBalances?: Record<string, LocationBalance[]>;
  loadingIcsBalances?: boolean;
  sourceWarehouse?: string;
  stockAdjustments?: Map<string, number>;
}

// ===== Weight mode helper functions =====

const EMPTY_WEIGHT_ENTRY: WeightMaterialEntry = {
  item_number: '', transfer_number: '', item_name: '', allocated_weight: '',
  available_stock: 0, yield_factor: 0, unit: 'lb', is_on_hold: false,
};

export const createEmptyWeightEntry = (): WeightMaterialEntry => ({ ...EMPTY_WEIGHT_ENTRY });

export const calculateEstimatedBoxes = (entry: WeightMaterialEntry, products: Product[], productSku: string): number => {
  if (!entry.allocated_weight) return 0;
  const lbs = parseFloat(entry.allocated_weight) || 0;
  if (lbs <= 0) return 0;
  // Use raw material unit_weight (lbs/case of the raw material input) when available.
  // This is the correct divisor: how many raw-material cases does the requested weight represent?
  // Falling back to product unit_weight was wrong — e.g. 310 lbs / 1 lb-product = 310 cases
  // instead of 310 lbs / 50 lbs-per-case-raw = 7 cases.
  const rawUnitWeight = entry.unit_weight ?? 0;
  if (rawUnitWeight > 0) return Math.ceil(lbs / rawUnitWeight);
  const product = products.find(p => p.sku === productSku);
  if (!product?.unit_weight) return 0;
  return Math.ceil(lbs / product.unit_weight);
};

export const calculateEstUsable = (entry: WeightMaterialEntry): number => {
  if (!entry.allocated_weight || !entry.yield_factor) return 0;
  const lbs = parseFloat(entry.allocated_weight) || 0;
  return lbs * entry.yield_factor;
};

export const calculateWeightTotals = (
  weightEntries: WeightMaterialEntry[],
  products: Product[],
  productSku: string,
): { totalLbs: number; totalBoxes: number; totalOutputWeight: number } => {
  const product = products.find(p => p.sku === productSku);
  let totalLbs = 0;
  let totalOutputWeight = 0;

  weightEntries.forEach(entry => {
    if (entry.allocated_weight && entry.item_number) {
      const lbs = parseFloat(entry.allocated_weight) || 0;
      totalLbs += lbs;
      if (entry.yield_factor) {
        totalOutputWeight += lbs * entry.yield_factor;
      }
    }
  });

  const totalBoxes = product?.unit_weight ? Math.floor(totalOutputWeight / product.unit_weight) : 0;
  return { totalLbs, totalBoxes, totalOutputWeight };
};

export const hasWeightValidationErrors = (weightEntries: WeightMaterialEntry[]): boolean => {
  return weightEntries.some(entry => {
    if (!entry.item_number || !entry.allocated_weight) return false;
    if (!entry.transfer_number) return false;
    const lbs = parseFloat(entry.allocated_weight) || 0;
    return lbs > entry.available_stock;
  });
};

export const isWeightFormValid = (
  weightEntries: WeightMaterialEntry[],
  productSku: string,
  sourceWarehouse: string,
): boolean => {
  if (!productSku) return false;
  const selectedMaterials = weightEntries.filter(entry => entry.item_number);
  const hasValidMaterial = selectedMaterials.length > 0 && selectedMaterials.every(entry =>
    entry.allocated_weight && parseFloat(entry.allocated_weight) > 0
  );
  if (!hasValidMaterial) return false;
  const needsTransfer = selectedMaterials.some(e =>
    !e.transfer_number || (parseFloat(e.allocated_weight) || 0) > e.available_stock
  );
  if (needsTransfer && !sourceWarehouse) return false;
  return true;
};

const WeightMaterialSection: React.FC<WeightMaterialSectionProps> = ({
  weightEntries,
  setWeightEntries,
  productSku,
  products,
  rawMaterials,
  sortedRawMaterials,
  loadingMaterials,
  materialSortOption,
  setMaterialSortOption,
  byproductMappings,
  materialSummary,
  icsBalances,
  loadingIcsBalances,
  sourceWarehouse,
  stockAdjustments,
}) => {
  const { t } = useLanguage();

  const updateWeightMaterial = (index: number, field: string, value: string) => {
    if (field === 'material') {
      if (!value) {
        setWeightEntries(prev => prev.map((entry, i) =>
          i === index ? createEmptyWeightEntry() : entry
        ));
        return;
      }
      const [itemNumber, transferNumberRaw] = value.split('|');
      const actualTransferNumber = transferNumberRaw || null;
      const material = rawMaterials.find(
        m => m.item_number === itemNumber && m.transfer_number === actualTransferNumber
      );
      if (material) {
        setWeightEntries(prev => prev.map((entry, i) => {
          if (i !== index) return entry;
          const defaultWeight = !actualTransferNumber ? '0' : entry.allocated_weight;
          return {
            item_number: itemNumber,
            transfer_number: actualTransferNumber || '',
            item_name: material.item_english_name || '',
            allocated_weight: defaultWeight,
            available_stock: material.current_stock,
            yield_factor: material.yield_factor || 0,
            yield_factor_source: material.yield_factor_source,
            unit: material.unit || 'lb',
            // Raw material unit weight (lbs/case) — ensures calculateEstimatedBoxes
            // divides by the correct per-case weight, not the finished product weight.
            unit_weight: parseFloat(String(material.unit_weight)) || undefined,
            is_on_hold: material.is_on_hold || false,
          };
        }));
      }
    } else if (field === 'allocated_weight') {
      setWeightEntries(prev => prev.map((entry, i) =>
        i === index ? { ...entry, allocated_weight: value } : entry
      ));
    }
  };

  const getByproductsForMaterial = (itemNumber: string): ByproductMapping[] => {
    if (!itemNumber || !byproductMappings.length) return [];
    return byproductMappings.filter(m => m.raw_material_item_number === itemNumber);
  };

  const weightTotals = calculateWeightTotals(weightEntries, products, productSku);

  // Build set of selected material keys for the overview table
  const selectedItemKeys = useMemo(() => {
    const keys = new Set<string>();
    weightEntries.forEach(e => {
      if (e.item_number) {
        keys.add(`${e.item_number}|${e.transfer_number || ''}|${e.location_code || ''}`);
        keys.add(`${e.item_number}||`);
      }
    });
    return keys;
  }, [weightEntries]);

  // Handle clicking a row in the overview table
  const handleOverviewSelect = (material: RawMaterial) => {
    const key = `${material.item_number}|${material.transfer_number || ''}|${material.location_code || ''}`;

    // If already selected, deselect it (remove the entry)
    const existingIdx = weightEntries.findIndex(
      e => e.item_number === material.item_number && e.transfer_number === (material.transfer_number || '') && (e.location_code || '') === (material.location_code || '')
    );
    if (existingIdx >= 0) {
      if (weightEntries.length <= 1) {
        setWeightEntries([createEmptyWeightEntry()]);
      } else {
        setWeightEntries(prev => prev.filter((_, i) => i !== existingIdx));
      }
      return;
    }

    // Find the first empty entry slot
    const emptyIdx = weightEntries.findIndex(e => !e.item_number);
    const defaultWeight = !material.transfer_number ? '0' : '';
    const newEntry: WeightMaterialEntry = {
      item_number: material.item_number,
      transfer_number: material.transfer_number || '',
      item_name: material.item_english_name || '',
      allocated_weight: defaultWeight,
      available_stock: material.current_stock,
      yield_factor: material.yield_factor || 0,
      yield_factor_source: material.yield_factor_source,
      unit: material.unit || 'lb',
      is_on_hold: material.is_on_hold || false,
      location_code: material.location_code || '',
    };

    if (emptyIdx >= 0) {
      setWeightEntries(prev => prev.map((e, i) => i === emptyIdx ? newEntry : e));
    } else {
      setWeightEntries(prev => [...prev, newEntry]);
    }
  };

  const selectedEntries = weightEntries.filter(e => e.item_number);

  return (
    <div className="space-y-3">
      {/* Header with sort */}
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('supervisor.material')} *
        </label>
        <div className="flex items-center gap-1.5">
          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
          <select
            value={materialSortOption}
            onChange={(e) => setMaterialSortOption(e.target.value as MaterialSortOption)}
            disabled={!productSku}
            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
          >
            <option value="yield_high">{t('supervisor.sortYieldHigh') || 'Yield ↑'}</option>
            <option value="yield_low">{t('supervisor.sortYieldLow') || 'Yield ↓'}</option>
            <option value="availability_high">{t('supervisor.sortAvailabilityHigh') || 'Stock ↑'}</option>
            <option value="availability_low">{t('supervisor.sortAvailabilityLow') || 'Stock ↓'}</option>
          </select>
        </div>
      </div>

      {/* MaterialSummaryBoxes removed — total shown at bottom of RawMaterialOverview */}

      {/* Raw Material Overview Table — click to select/deselect */}
      {productSku && (
        <RawMaterialOverview
          sortedRawMaterials={sortedRawMaterials}
          selectedItemKeys={selectedItemKeys}
          onSelectMaterial={handleOverviewSelect}
          loading={loadingMaterials}
          icsBalances={icsBalances}
          loadingIcsBalances={loadingIcsBalances}
          sourceWarehouse={sourceWarehouse}
          stockAdjustments={stockAdjustments}
        />
      )}

      {!productSku && (
        <p className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">
          {t('common.selectProductFirst') || 'Select a finished product first'}
        </p>
      )}


      {/* Selected Material Detail Cards */}
      {selectedEntries.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {t('supervisor.selectedMaterials') || 'Selected Materials'}
          </div>

          {weightEntries.map((entry, index) => {
            if (!entry.item_number) return null;
            const estUsable = calculateEstUsable(entry);
            const estBoxes = calculateEstimatedBoxes(entry, products, productSku);
            const lbs = parseFloat(entry.allocated_weight) || 0;
            const weightExceedsStock = entry.item_number && entry.allocated_weight && lbs > entry.available_stock && entry.transfer_number;
            const yieldValue = entry.yield_factor ? parseFloat(String(entry.yield_factor)) : null;
            const isEntryFallback = isYieldFallback(entry.yield_factor_source);
            const yieldDisplay = yieldValue ? formatYield(yieldValue, undefined, entry.yield_factor_source) : null;
            const rawMat = rawMaterials.find(m => m.item_number === entry.item_number);
            const rawUnitWeight = rawMat?.unit_weight || 0;
            const estCasesNeeded = (lbs > 0 && rawUnitWeight > 0) ? Math.ceil(lbs / rawUnitWeight) : 0;
            const sourceLoc = sourceWarehouse
              ? icsBalances?.[entry.item_number]?.find(l => l.locationCode === sourceWarehouse)
              : undefined;
            const sourceAvailCases = sourceLoc?.displayCasesPieces?.cases ?? null;
            const exceedsSourceBalance = sourceAvailCases !== null && estCasesNeeded > 0 && estCasesNeeded > sourceAvailCases;

            return (
              <div key={index} className="p-3 border border-indigo-200 dark:border-indigo-700/50 rounded-lg bg-indigo-50/50 dark:bg-indigo-900/20 space-y-3">
                {/* Material header with name and remove button */}
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    {index === 0 && (
                      <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/40 px-2 py-0.5 rounded">
                        {t('supervisor.primaryMaterial') || 'Primary Material'}
                      </span>
                    )}
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {entry.item_number} - {entry.item_name}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleOverviewSelect(rawMaterials.find(
                      m => m.item_number === entry.item_number && m.transfer_number === (entry.transfer_number || null)
                    ) || { item_number: entry.item_number, transfer_number: entry.transfer_number || null } as RawMaterial)}
                    className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    title="Remove material"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Material Info and Input Row */}
                <div className="grid grid-cols-5 gap-3">
                  {/* Available Stock */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.availableStock') || 'Available'}</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{entry.available_stock.toFixed(2)} {entry.unit}</p>
                  </div>

                  {/* Yield Factor */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('operator.yield')}</span>
                    <p className={`text-sm font-medium ${isEntryFallback ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>{yieldDisplay || '-'}</p>
                  </div>

                  {/* Weight Input (lbs) */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.lbsInput') || 'Weight (lbs)'}</span>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        placeholder="0"
                        value={entry.allocated_weight}
                        onChange={(e) => updateWeightMaterial(index, 'allocated_weight', e.target.value)}
                        className={`w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm ${
                          weightExceedsStock
                            ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                        } text-gray-900 dark:text-white`}
                      />
                    </div>
                    {weightExceedsStock && (
                      <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">{t('supervisor.partialTransferNeeded') || 'Transfer needed'}</p>
                    )}
                  </div>

                  {/* Est. Usable Output */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.estUsableMaterial') || 'Est. Usable (lbs)'}</span>
                    <p className="text-sm font-medium text-green-600 dark:text-green-400">
                      {estUsable > 0 ? estUsable.toFixed(2) : '-'}
                    </p>
                  </div>

                  {/* Est. Boxes */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.estimatedBoxes') || 'Est. Boxes'}</span>
                    <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                      {estBoxes > 0 ? estBoxes : '-'}
                    </p>
                  </div>
                </div>

                {/* VB6 source balance warning */}
                {exceedsSourceBalance && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700">
                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <p className="text-sm font-medium text-red-700 dark:text-red-300">
                      {t('supervisor.insufficientSourceStock') || 'Insufficient stock at'} {sourceLoc?.locationName || sourceWarehouse} ({sourceWarehouse}): {t('supervisor.onlyHas') || 'only has'} {sourceAvailCases} CS — {estCasesNeeded} CS {t('supervisor.needed') || 'needed'}
                    </p>
                  </div>
                )}

                {/* Linked By-Products */}
                {(() => {
                  const linkedBp = getByproductsForMaterial(entry.item_number);
                  if (!linkedBp.length) return null;
                  return (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2">
                      <span className="text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded">
                        {t('supervisor.byproducts')}
                      </span>
                      {linkedBp.map((bp, idx) => (
                        <React.Fragment key={bp.id}>
                          {idx > 0 && <span className="text-xs text-gray-300 dark:text-gray-600">|</span>}
                          <span className="text-xs text-gray-700 dark:text-gray-300">
                            {bp.byproduct_item_number}{bp.byproduct_name ? ` - ${bp.byproduct_name}` : ''}{bp.byproduct_chinese_name ? ` / ${bp.byproduct_chinese_name}` : ''}
                          </span>
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {/* Totals Summary */}
      {weightEntries.some(e => e.item_number && e.allocated_weight) && (
        <div className="flex justify-end gap-6 pt-2 border-t border-gray-200 dark:border-gray-600">
          <div className="text-sm">
            <span className="text-gray-500 dark:text-gray-400">{t('supervisor.totalLbs') || 'Total Weight'}:</span>
            <span className="ml-2 font-medium text-orange-600 dark:text-orange-400">{weightTotals.totalLbs.toFixed(2)} lbs</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-500 dark:text-gray-400">{t('supervisor.totalEstUsable') || 'Est. Usable'}:</span>
            <span className="ml-2 font-medium text-green-600 dark:text-green-400">{weightTotals.totalOutputWeight.toFixed(2)} lbs</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-500 dark:text-gray-400">{t('supervisor.totalBoxes') || 'Est. Boxes'}:</span>
            <span className="ml-2 font-medium text-indigo-600 dark:text-indigo-400">{weightTotals.totalBoxes}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeightMaterialSection;
